/**
 * Cartridge launcher.
 *
 * Hosts a single cartridge from preload through teardown. Extracted from
 * the cabinet's MVP auto-launch path so the selector (sc-2818) can
 * launch ANY `GameModule`, not just the hardcoded NEON OVERPASS.
 *
 * Lifecycle inside one launch:
 *   1. `module.preload(preloadCtx)` runs (assets, sfx recipes — noop today)
 *   2. Set cabinet to `playing` mode (chrome collapses)
 *   3. Run rounds in a loop: each round builds a fresh GameContext with
 *      a new PRNG seed, creates an instance, drives the runtime loop
 *      until either the cartridge requests a restart OR fires `ctx.exit`
 *   4. On `ctx.exit`: stop the loop, flip cabinet back to attract, return
 *
 * The "restart" convention (`NeonOverpassInstance.shouldRestart()`) is a
 * carry-over from the MVP. The cabinet's unified GAME OVER flow will
 * replace it with proper `ctx.exit('game_over')` → restart-prompt under
 * sc-2819 (back-nav). For now, a duck-typed `shouldRestart()` check
 * keeps cartridges responsible for their own retry UX.
 */

import type { ExitReason, GameModule, PreloadContext } from '@arcraz/arcadia-types';
import { ApiError, getDailySeed, submitScore } from '../api/client.js';
import { track } from './analytics.js';
import { setCabinetMode } from './cabinet-mode.js';
import {
  getVisibleCanvas,
  setLogicalResolution,
  setSmoothing,
} from './compositor.js';
import { buildGameContext, getCabinetAudioBus } from './context.js';
import {
  attachPauseButton,
  detachPauseButton,
  setPauseButtonVisible,
} from './pause-button.js';
import {
  attachMuteButton,
  detachMuteButton,
  setMuteButtonVisible,
} from './mute-button.js';
import {
  attachTouchControls,
  detachTouchControls,
  setTouchControlsVisible,
} from './input-touch.js';
import {
  attachPointer,
  detachPointer,
  setPointerLogicalResolution,
} from './input-pointer.js';
import { pushToast } from './toast.js';
import {
  getLastInitials,
  getOrCreateDeviceId,
  setLastInitials,
} from './identity.js';
import { runInitialsEntry } from './initials-overlay.js';
import type { InputSource } from './loop.js';
import type { KeyboardInputSource } from './input-keyboard.js';
import { startLoop } from './loop.js';
import { runPauseOverlay } from './pause-overlay.js';
import { runPressStart } from './press-start.js';

/**
 * Generate a fresh PRNG seed for a new round. NOT replay-deterministic on
 * its own (Date.now is intentional — different runs should have different
 * seeds); cartridges receive the seed and from that point forward MUST
 * be deterministic.
 */
function newSeed(): string {
  return `local-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
}

/**
 * Build a PreloadContext for the cartridge. The `registerSfx` channel
 * forwards to the cabinet's shared AudioBus so cartridges can register
 * their named SFX recipes once at preload time.
 */
function buildPreloadCtx(): PreloadContext {
  return {
    async loadImage(): Promise<ImageBitmap> {
      throw new Error('loadImage not yet implemented — needs asset-loader story');
    },
    async loadAudio(): Promise<ArrayBuffer> {
      throw new Error('loadAudio not yet implemented — needs audio-bus story');
    },
    registerSfx(name: string, recipe: unknown): void {
      // PreloadContext's `recipe` shape is opaque to the runtime types —
      // the cartridge owns what it registers; we forward as-is. The
      // audio bus validates the recipe shape at play time.
      getCabinetAudioBus().registerSfx(
        name,
        recipe as Parameters<ReturnType<typeof getCabinetAudioBus>['registerSfx']>[1]
      );
    },
  };
}

/**
 * Cabinet convention helper: some cartridges expose `shouldRestart(): boolean`
 * to ask the cabinet to tear down and start a fresh round on player input.
 * Duck-typed so any cartridge can opt in without a new contract surface.
 */
function hasShouldRestart(instance: unknown): instance is { shouldRestart(): boolean } {
  return (
    typeof instance === 'object' &&
    instance !== null &&
    'shouldRestart' in instance &&
    typeof (instance as { shouldRestart: unknown }).shouldRestart === 'function'
  );
}

/**
 * Cabinet convention: cartridges may expose `isGameOver(): boolean` so the
 * launcher can submit the score the moment the run actually ends, not
 * when the player presses Z to retry (which can be much later). Duck-typed
 * — cartridges without it fall back to "submit on round-end" behavior.
 */
function hasIsGameOver(instance: unknown): instance is { isGameOver(): boolean } {
  return (
    typeof instance === 'object' &&
    instance !== null &&
    'isGameOver' in instance &&
    typeof (instance as { isGameOver: unknown }).isGameOver === 'function'
  );
}

export interface LaunchOpts {
  /** Optional callback when the cartridge title changes; cabinet uses this
   *  to update the control-panel readout. */
  onTitle?: (title: string) => void;
  /** Optional pre-launch hook. Cabinet uses this to e.g. flip into
   *  `playing` mode before the loop starts. Defaults to setCabinetMode. */
  onEnter?: () => void;
  /** Optional post-exit hook. Cabinet uses this to e.g. flip back to
   *  `attract` mode. Defaults to setCabinetMode. */
  onExit?: (reason: ExitReason) => void;
  /** Register a pause-request channel — the cabinet's Escape listener
   *  invokes the returned function. The launcher will pause its loop,
   *  show the pause overlay, and either resume or exit based on the
   *  player's choice. Called once per cartridge launch. */
  registerPauseRequest?: (requestPause: () => void) => () => void;
  /** When set, every round in this launch uses the daily-challenge
   *  seed for this UTC date instead of a fresh random seed (sc-3016).
   *  The launcher fetches it lazily at round start; failure falls back
   *  to a random seed + console warning so the player isn't stranded
   *  if the API is down. Scores submitted in this mode include
   *  `dailySeedDate` so the server can validate and bucket them onto
   *  the daily leaderboard. */
  dailyChallengeDate?: string;
}

/**
 * Launch a cartridge and run it until the cartridge fires `ctx.exit()`.
 * Returns the exit reason so the cabinet state machine can decide where
 * to go next (selector / game-over / etc.).
 */
export async function launchCartridge(
  module_: GameModule,
  ctx2d: CanvasRenderingContext2D,
  input: KeyboardInputSource,
  opts: LaunchOpts = {}
): Promise<ExitReason> {
  opts.onTitle?.(module_.title);
  (opts.onEnter ?? (() => setCabinetMode('playing')))();

  // Resize the canvas backing store to the cartridge's declared logical
  // resolution. The bezel CSS handles display scaling (16:9 letterbox,
  // object-fit: contain), so all this changes is the coordinate space the
  // cartridge draws into. Restored to the cabinet baseline (480x270) in
  // the `finally` block so the selector/attract/boot screens render at
  // their native pixel size when we exit.
  const prevCanvasW = ctx2d.canvas.width;
  const prevCanvasH = ctx2d.canvas.height;
  // Compositor: resize the offscreen surface to the cartridge's logical
  // resolution AND toggle the visible-canvas blit smoothing accordingly.
  // Pixel cartridges blit nearest-neighbor (crisp); vector cartridges
  // blit bilinear (smooth strokes at any scale). `setLogicalResolution`
  // mutates the canvas the offscreen ctx is attached to, which resets
  // smoothing — re-apply for the cartridge's own draw calls.
  setLogicalResolution(module_.resolution.w, module_.resolution.h);
  setSmoothing(!module_.pixelated);
  ctx2d.imageSmoothingEnabled = !module_.pixelated;

  // Pointer capture — bind to the VISIBLE canvas (pointer events fire
  // on the DOM-attached node, not the offscreen one) and tell the
  // pointer module what logical resolution to map into. Cartridges
  // then read InputState.pointer.{x,y,down} in their logical coords.
  setPointerLogicalResolution(module_.resolution.w, module_.resolution.h);
  attachPointer(getVisibleCanvas());

  await module_.preload(buildPreloadCtx());

  let exitReason: ExitReason = 'player_quit';
  let running = true;
  let pauseRequested = false;
  // Captured in the onExit closure so the poll loop can react. We set
  // currentHandle each round; onExit stops it so the poll resolves.
  let currentHandle: ReturnType<typeof startLoop> | null = null;

  // Register the pause-request channel. Cabinet's Escape listener will
  // invoke this; we flip a flag the poll loop checks each frame. The
  // in-game pause button (touch users) fires the same flag via the
  // pause-button module attached below.
  const unregisterPause = opts.registerPauseRequest?.(() => {
    pauseRequested = true;
  });

  // Attach the on-canvas pause button. Lives for the entire cartridge
  // session; the button's pointer listener fires the same pauseRequested
  // flag the keyboard Escape listener uses, so touch users have the
  // same pause/quit path keyboard users do.
  attachPauseButton({
    onRequest: () => {
      pauseRequested = true;
    },
  });

  // Mute button — sits above pause button, toggles the cabinet audio
  // bus's muted flag. Same lifecycle as the pause button.
  attachMuteButton();

  // Attach on-screen touch controls (joystick + per-cartridge action
  // buttons). Auto-visible on touch devices, toggleable with T anywhere
  // for desktop testing. Drives the SAME keyboard input source so
  // cartridges see a unified InputState. Pulls per-cartridge button
  // labels from `module_.touch`.
  attachTouchControls({ input, module_ });

  let firstRound = true;

  try {
    while (running) {
      // Daily mode: fetch the deterministic seed for the requested
      // UTC date. Fall back to a random seed + warn if the API can't
      // hand one back — don't strand the player in a black screen.
      let seed = newSeed();
      let dailySeedDate: string | null = null;
      if (opts.dailyChallengeDate) {
        try {
          const ds = await getDailySeed(module_.id, opts.dailyChallengeDate);
          seed = ds.seed;
          dailySeedDate = ds.date;
        } catch (err) {
          // sc-3123: the random-seed fallback below keeps the player
          // from being stranded in a black screen, BUT a run started
          // this way submits with `dailySeedDate = null`, so the daily
          // leaderboard filters it out server-side. The player picked
          // daily mode expecting to compete on today's challenge —
          // tell them their run won't count for that board.
          // eslint-disable-next-line no-console
          console.warn('[shell] daily seed fetch failed; falling back to random', err);
          pushToast('DAILY UNAVAILABLE — ARCADE MODE', { variant: 'error' });
        }
      }
      const gameContext = buildGameContext({
        seed,
        audio: getCabinetAudioBus(),
        onExit: (reason: ExitReason) => {
          exitReason = reason;
          running = false;
          // Stop the loop so the poll-await below resolves immediately;
          // otherwise the cartridge keeps ticking but the outer while
          // never re-evaluates `running`, and the player is stuck.
          currentHandle?.stop();
        },
      });
      const instance = module_.init(gameContext, { seed, mode: 'arcade' });

      // Press-to-start gate (sc-2823) on FIRST round only. The cartridge's
      // initial frame paints under the overlay so the player sees the
      // actual game waiting for them. shouldRestart() rounds skip the gate
      // — player already pressed Z to restart, asking them to press it
      // again would be redundant. Determinism: instance.render() never
      // consumes PRNG; instance.update() hasn't fired yet, so PRNG state
      // at gate release is identical to "launched immediately."
      if (firstRound) {
        firstRound = false;
        // Hide pause button + mute + touch controls during the press-
        // start gate — the cartridge isn't actually playing yet, the
        // gate consumes any tap directly to advance.
        setPauseButtonVisible(false);
        setMuteButtonVisible(false);
        setTouchControlsVisible(false);
        await runPressStart(ctx2d, input, module_, instance);
        setPauseButtonVisible(true);
        setMuteButtonVisible(true);
        setTouchControlsVisible(true);
      }

      const handle = startLoop(instance, ctx2d, input);
      currentHandle = handle;

      // Tracks whether this round's score has already been submitted.
      // Set when `isGameOver()` first returns true and we kick off the
      // submission — prevents double-submit if the player lingers on the
      // game-over screen, opens the pause menu, then presses Z.
      let scoreSubmitted = false;

      await new Promise<void>((resolve) => {
        const poll = async (): Promise<void> => {
          if (!handle.isRunning()) {
            resolve();
            return;
          }
          // Game-over detected. Submit IMMEDIATELY (not on player
          // restart/quit) so the leaderboard the player might open via
          // the pause menu reflects the run that just ended.
          //
          // We pause the runtime + instance while the initials overlay
          // runs so the cartridge's game-over input handler (Z arms
          // restart) doesn't fight with the overlay's input handler.
          // After submission, resume the loop — the player can still
          // press Z to retry from the game-over panel.
          if (
            !scoreSubmitted &&
            hasIsGameOver(instance) &&
            instance.isGameOver()
          ) {
            scoreSubmitted = true;
            handle.pause();
            instance.pause();
            // Hide the chrome pause button + mute + touch controls
            // during the initials overlay. The overlay handles its own
            // taps.
            setPauseButtonVisible(false);
            setMuteButtonVisible(false);
            setTouchControlsVisible(false);
            await handleRoundEndScore(
              ctx2d,
              input,
              module_,
              instance,
              seed,
              dailySeedDate
            );
            setPauseButtonVisible(true);
            setMuteButtonVisible(true);
            setTouchControlsVisible(true);
            instance.resume();
            handle.resume();
            // The Z press the player used to confirm initials may also
            // have been seen by the cartridge as "arm restart" before
            // we paused. Drop it: poll one tick fresh so any held keys
            // are cleared by the next snapshot diff.
            requestAnimationFrame(poll);
            return;
          }
          if (hasShouldRestart(instance) && instance.shouldRestart()) {
            handle.stop();
            resolve();
            return;
          }
          if (pauseRequested) {
            pauseRequested = false;
            // Pause the runtime loop. Cartridge's update() will stop
            // ticking; rendering also stops (the last frame stays on
            // canvas, and the overlay draws over it).
            handle.pause();
            instance.pause();
            // Hide the chrome pause button + mute + touch controls
            // while the overlay is up — taps on the overlay are
            // handled directly by the overlay (the overlay has its own
            // MUTE row), and re-tapping pause would queue another
            // pause-on-resume.
            setPauseButtonVisible(false);
            setMuteButtonVisible(false);
            setTouchControlsVisible(false);
            const choice = await runPauseOverlay(ctx2d, input, module_, instance);
            // The cabinet-level Escape listener can fire `requestPause()`
            // again WHILE the overlay is open (after its 500ms throttle
            // expires). The overlay itself consumes Escape as "resume",
            // but the launcher listener sets `pauseRequested = true` in
            // parallel — without this clear, we'd immediately re-open
            // the overlay one tick after resume.
            pauseRequested = false;
            if (choice === 'resume') {
              setPauseButtonVisible(true);
              setMuteButtonVisible(true);
              setTouchControlsVisible(true);
              instance.resume();
              handle.resume();
              requestAnimationFrame(poll);
              return;
            }
            // 'quit' — stop the round, signal the outer loop to exit.
            // detachPauseButton in the outer finally hides it.
            handle.stop();
            exitReason = 'player_quit';
            running = false;
            resolve();
            return;
          }
          requestAnimationFrame(poll);
        };
        void poll();
      });

      // Round ended (cartridge stopped or exited). If we already
      // submitted from the isGameOver() path above, skip — no
      // double-submit. Cartridges that don't expose isGameOver() (no
      // duck-type match) fall through here for end-of-round submission.
      //
      // Skip on 'player_quit' too: the player tapped RETURN TO SELECTOR
      // mid-run, they're not asking for their partial score on the
      // board. Also avoids rendering a torn-down instance — handle.stop()
      // above already called instance.teardown(), so handleRoundEndScore's
      // re-render-the-cartridge call would crash.
      if (!scoreSubmitted && exitReason !== 'player_quit') {
        await handleRoundEndScore(
          ctx2d,
          input,
          module_,
          instance,
          seed,
          dailySeedDate
        );
      }
    }
  } finally {
    unregisterPause?.();
    detachPauseButton();
    detachMuteButton();
    detachTouchControls();
    detachPointer();
    // Restore the compositor to cabinet baseline so the selector / boot /
    // attract screens render at their native resolution next round.
    setLogicalResolution(prevCanvasW, prevCanvasH);
    setSmoothing(false);
    ctx2d.imageSmoothingEnabled = false;
  }

  // Default onExit is a no-op — caller controls cabinet mode after exit.
  // Previously this flipped to `attract` unconditionally, which caused
  // a visible marquee/panel flash when the caller's next action was to
  // re-enter `selecting` mode (the runCabinet loop). Caller knows where
  // it's going next; let it pick the right mode without us guessing.
  (opts.onExit ?? ((_r) => {}))(exitReason);
  return exitReason;
}

/**
 * Post-round score submission. Triggers the initials-entry overlay (with
 * the player's last-used initials pre-filled), then POSTs to /v1/scores
 * with this device's ID. Anonymous-only in v1 (sc-3014); when sc-3015
 * adds Zyphr login, the API client will start attaching the Bearer
 * token automatically and the server will route on that.
 *
 * Wraps everything in try/catch so a network or API failure doesn't
 * brick the cartridge's restart/quit loop — the player can still play
 * even if the API is down.
 */
async function handleRoundEndScore(
  ctx2d: CanvasRenderingContext2D,
  input: InputSource,
  module_: GameModule,
  instance: {
    getFinalScore?: () => number;
    getScore?: () => number;
    render: (g: CanvasRenderingContext2D) => void;
  },
  seed: string,
  dailySeedDate: string | null
): Promise<void> {
  const finalScore =
    typeof instance.getFinalScore === 'function'
      ? instance.getFinalScore()
      : typeof instance.getScore === 'function'
        ? instance.getScore()
        : 0;
  if (!Number.isFinite(finalScore) || finalScore <= 0) return;

  try {
    // Re-render the cartridge's last frame so the initials overlay can
    // scrim over it (matches the pause-overlay convention). Guarded —
    // if the cartridge's render throws (e.g., instance was torn down,
    // or its render path has a bug at end-state), fall back to a black
    // canvas. Better than aborting the whole submission flow.
    try {
      instance.render(ctx2d);
    } catch (renderErr) {
      // eslint-disable-next-line no-console
      console.warn('[shell] cartridge render failed on game-over re-render', renderErr);
      ctx2d.fillStyle = '#000';
      ctx2d.fillRect(0, 0, ctx2d.canvas.width, ctx2d.canvas.height);
    }
    const initials = await runInitialsEntry(ctx2d, input, {
      score: finalScore,
      defaultInitials: getLastInitials() ?? 'AAA',
    });
    setLastInitials(initials);

    await submitScore({
      cartridgeId: module_.id,
      cartridgeVersion: module_.validatorVersion,
      score: Math.floor(finalScore),
      initials,
      seed,
      inputFrames: [],
      deviceId: getOrCreateDeviceId(),
      ...(dailySeedDate ? { dailySeedDate } : {}),
    });
    track('score_submitted', {
      cartridgeId: module_.id,
      cartridgeVersion: module_.validatorVersion,
      score: Math.floor(finalScore),
      isDaily: dailySeedDate !== null,
      ...(dailySeedDate ? { dailySeedDate } : {}),
    });
  } catch (err) {
    // sc-3122: surface a toast so the player knows the score didn't
    // land. We still log to console for dev observability — the toast
    // is informational and dismisses on its own. A future story will
    // add a retry queue + "tap to retry" affordance.
    if (err instanceof ApiError) {
      // eslint-disable-next-line no-console
      console.warn('[shell] score submission failed', {
        status: err.status,
        code: err.code,
        message: err.message,
      });
      track('score_submission_failed', {
        cartridgeId: module_.id,
        status: err.status,
        code: err.code,
      });
    } else {
      // eslint-disable-next-line no-console
      console.warn('[shell] score submission error', err);
      track('score_submission_failed', {
        cartridgeId: module_.id,
        error: err instanceof Error ? err.message : 'unknown',
      });
    }
    pushToast('SCORE NOT SAVED', { variant: 'error' });
  }
}
